<div class="d-flex justify-content-between align-items-center mb-3">
    <h3 class="mb-0">Invoicing</h3>
    @if (canEdit) {
        <div class="d-flex gap-2">
            <button class="btn btn-outline-secondary btn-sm" (click)="toggleUnmatchedFilter()" [class.active]="showUnmatchedOnly">
                {{ showUnmatchedOnly ? 'Show All' : 'Unmatched Only' }}
            </button>
            <button class="btn btn-primary btn-sm" (click)="startNew()">+ New Invoice</button>
        </div>
    }
</div>

<input #invoiceInput type="file" class="d-none" accept=".pdf,.png,.jpg,.jpeg,.docx,.xlsx" (change)="onInvoiceFileSelected($event)" />
<input #docInput type="file" class="d-none" multiple (change)="onDocFileSelected($event)" />

<div class="row g-3">
    <div class="col-lg-5">
        <div class="purchasing-table-panel">
            <table class="table table-sm table-hover mb-0 invoice-list">
                <thead>
                    <tr>
                        <th>Invoice #</th>
                        <th>PO #</th>
                        <th>Vendor</th>
                        <th>Source</th>
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    @for (invoice of filteredInvoices; track invoice.id) {
                        <tr (click)="selectInvoice(invoice)" [class.table-active]="selectedInvoice?.id === invoice.id" style="cursor: pointer">
                            <td>{{ invoice.invoiceNumber || '—' }}</td>
                            <td>
                                @if (invoice.poNumber) {
                                    <a [routerLink]="['/purchasing/purchase-orders']" (click)="$event.stopPropagation()">{{ invoice.poNumber }}</a>
                                } @else {
                                    <span class="badge bg-warning text-dark">Unmatched</span>
                                }
                            </td>
                            <td>{{ invoice.vendorName }}</td>
                            <td><span class="badge" [ngClass]="sourceBadgeClass(invoice.source)">{{ invoice.source || 'Manual' }}</span></td>
                            <td><span class="badge" [ngClass]="statusBadgeClass(invoice.status)">{{ invoice.status }}</span></td>
                        </tr>
                    }
                    @if (filteredInvoices.length === 0) {
                        <tr>
                            <td colspan="5" class="text-center text-muted py-3">No invoices yet.</td>
                        </tr>
                    }
                </tbody>
            </table>
        </div>
    </div>
    <div class="col-lg-7">
        @if (editing && editModel) {
            <div class="card">
                <div class="card-header d-flex justify-content-between align-items-center">
                    <span>{{ creating ? 'New Invoice' : 'Edit Invoice' }}</span>
                    @if (canEdit) {
                        <button class="btn btn-outline-secondary btn-sm" (click)="triggerInvoiceScan()" [disabled]="isProcessingAi">
                            @if (isProcessingAi) {
                                <span class="spinner-border spinner-border-sm me-1"></span>
                            }
                            Scan Invoice (AI)
                        </button>
                    }
                </div>
                <div class="card-body">
                    <div class="row g-2 mb-3">
                        <div class="col-md-6">
                            <label class="form-label small mb-0">Purchase Order (optional)</label>
                            <select class="form-select form-select-sm" [(ngModel)]="selectedOrderId" (ngModelChange)="onOrderSelected()">
                                <option value="">-- No PO / Unmatched --</option>
                                @for (po of openOrders; track po.id) {
                                    <option [value]="po.id">{{ po.poNumber }} - {{ po.vendorName }}{{ po.equipment ? ' / ' + po.equipment : '' }}{{ po.workOrder ? ' / WO ' + po.workOrder : '' }}</option>
                                }
                            </select>
                        </div>
                        <div class="col-md-3">
                            <label class="form-label small mb-0">Invoice #</label>
                            <input class="form-control form-control-sm" [(ngModel)]="editModel.invoiceNumber" />
                        </div>
                        <div class="col-md-3">
                            <label class="form-label small mb-0">Status</label>
                            <select class="form-select form-select-sm" [(ngModel)]="editModel.status">
                                @for (s of statuses; track s) {
                                    <option [value]="s">{{ s }}</option>
                                }
                            </select>
                        </div>
                        <div class="col-md-4">
                            <label class="form-label small mb-0">Vendor Name</label>
                            <input class="form-control form-control-sm" [(ngModel)]="editModel.vendorName" />
                        </div>
                        <div class="col-md-4">
                            <label class="form-label small mb-0">Invoice Date</label>
                            <input type="date" class="form-control form-control-sm" [(ngModel)]="editModel.invoiceDate" />
                        </div>
                        <div class="col-md-4">
                            <label class="form-label small mb-0">Due Date</label>
                            <input type="date" class="form-control form-control-sm" [(ngModel)]="editModel.dueDate" />
                        </div>
                        <div class="col-md-3">
                            <label class="form-label small mb-0">Subtotal</label>
                            <input type="number" class="form-control form-control-sm" [(ngModel)]="editModel.subtotal" />
                        </div>
                        <div class="col-md-3">
                            <label class="form-label small mb-0">Tax</label>
                            <input type="number" class="form-control form-control-sm" [(ngModel)]="editModel.taxAmount" />
                        </div>
                        <div class="col-md-3">
                            <label class="form-label small mb-0">Freight</label>
                            <input type="number" class="form-control form-control-sm" [(ngModel)]="editModel.freightAmount" />
                        </div>
                        <div class="col-md-3">
                            <label class="form-label small mb-0">Total</label>
                            <input type="number" class="form-control form-control-sm" [(ngModel)]="editModel.total" />
                        </div>
                        <div class="col-12">
                            <label class="form-label small mb-0">Notes</label>
                            <textarea class="form-control form-control-sm" rows="2" [(ngModel)]="editModel.notes"></textarea>
                        </div>
                    </div>

                    <div class="d-flex justify-content-between align-items-center mb-2">
                        <h6 class="mb-0">Line Items</h6>
                        <button class="btn btn-outline-secondary btn-sm" (click)="addLine()">+ Add Line</button>
                    </div>
                    <div class="table-responsive">
                        <table class="table table-sm align-middle line-item-table">
                            <thead>
                                <tr>
                                    <th>Description</th>
                                    <th class="text-end">Ordered</th>
                                    <th class="text-end">Received</th>
                                    <th class="text-end">Invoiced</th>
                                    <th class="text-end" style="width: 80px">Qty</th>
                                    <th class="text-end" style="width: 90px">Unit Price</th>
                                    <th class="text-end" style="width: 90px">Total</th>
                                    <th style="width: 30px"></th>
                                </tr>
                            </thead>
                            <tbody>
                                @for (line of editModel.lineItems; track $index; let i = $index) {
                                    <tr [ngClass]="lineInvoiceClass(line)">
                                        <td><input class="form-control form-control-sm" [(ngModel)]="line.description" /></td>
                                        <td class="text-end">{{ line.quantityOrdered ?? '—' }}</td>
                                        <td class="text-end">{{ line.quantityReceivedToDate ?? '—' }}</td>
                                        <td class="text-end">{{ line.quantityInvoicedToDate ?? '—' }}</td>
                                        <td>
                                            <input type="number" class="form-control form-control-sm text-end" [(ngModel)]="line.quantity" min="0" (ngModelChange)="updateLineTotal(line)" />
                                        </td>
                                        <td>
                                            <input type="number" class="form-control form-control-sm text-end" [(ngModel)]="line.unitPrice" min="0" (ngModelChange)="updateLineTotal(line)" />
                                        </td>
                                        <td class="text-end">{{ line.lineTotal | currency }}</td>
                                        <td>
                                            <button class="btn btn-link btn-sm text-danger p-0" (click)="removeLine(i)">&times;</button>
                                        </td>
                                    </tr>
                                }
                            </tbody>
                        </table>
                    </div>
                </div>
                <div class="card-footer d-flex justify-content-end gap-2">
                    <button class="btn btn-outline-secondary btn-sm" (click)="cancelEdit()">Cancel</button>
                    <button class="btn btn-primary btn-sm" (click)="save()" [disabled]="isSaving">
                        @if (isSaving) {
                            <span class="spinner-border spinner-border-sm me-1"></span>
                        }
                        Save
                    </button>
                </div>
            </div>
        } @else if (selectedInvoice) {
            <div class="card">
                <div class="card-header d-flex justify-content-between align-items-center">
                    <span>
                        {{ selectedInvoice.invoiceNumber || 'Invoice' }}
                        <span class="badge ms-2" [ngClass]="statusBadgeClass(selectedInvoice.status)">{{ selectedInvoice.status }}</span>
                        <span class="badge ms-1" [ngClass]="sourceBadgeClass(selectedInvoice.source)">{{ selectedInvoice.source || 'Manual' }}</span>
                    </span>
                    @if (canEdit) {
                        <div class="d-flex gap-2">
                            <button class="btn btn-outline-primary btn-sm" (click)="startEdit()">Edit</button>
                            <button class="btn btn-outline-secondary btn-sm" (click)="triggerDocUpload()">Upload Doc</button>
                            <button class="btn btn-outline-danger btn-sm" (click)="deleteInvoice(selectedInvoice)">Archive</button>
                        </div>
                    }
                </div>
                <div class="card-body">
                    @if (!selectedInvoice.isMatched && !selectedInvoice.purchaseOrderId) {
                        <div class="alert alert-warning py-2 small mb-3">
                            This invoice is not linked to a PO. Select a PO below to match it, or create a retroactive PO in Purchase Orders first.
                            <div class="d-flex gap-2 mt-2">
                                <select class="form-select form-select-sm" style="max-width: 280px" [(ngModel)]="selectedOrderId">
                                    <option value="">-- Select PO --</option>
                                    @for (po of openOrders; track po.id) {
                                        <option [value]="po.id">{{ po.poNumber }} - {{ po.vendorName }}{{ po.equipment ? ' / ' + po.equipment : '' }}{{ po.workOrder ? ' / WO ' + po.workOrder : '' }}</option>
                                    }
                                </select>
                                <button class="btn btn-sm btn-outline-primary" [disabled]="!selectedOrderId" (click)="linkPoToSelected()">Link PO</button>
                            </div>
                        </div>
                    }

                    <dl class="row mb-2 small">
                        <dt class="col-sm-3">PO</dt>
                        <dd class="col-sm-9">
                            @if (selectedInvoice.poNumber) {
                                <a [routerLink]="['/purchasing/purchase-orders']">{{ selectedInvoice.poNumber }}</a>
                            } @else {
                                <span class="text-muted">Unmatched</span>
                            }
                        </dd>
                        <dt class="col-sm-3">Vendor</dt>
                        <dd class="col-sm-9">{{ selectedInvoice.vendorName || '—' }}</dd>
                        <dt class="col-sm-3">Equipment</dt>
                        <dd class="col-sm-9">{{ selectedInvoice.equipment || '—' }}</dd>
                        <dt class="col-sm-3">Work Order</dt>
                        <dd class="col-sm-9">{{ selectedInvoice.workOrder || '—' }}</dd>
                        <dt class="col-sm-3">Invoice Date</dt>
                        <dd class="col-sm-9">{{ (selectedInvoice.invoiceDate | date: 'shortDate') || '—' }}</dd>
                        <dt class="col-sm-3">Due Date</dt>
                        <dd class="col-sm-9">{{ (selectedInvoice.dueDate | date: 'shortDate') || '—' }}</dd>
                        <dt class="col-sm-3">Total</dt>
                        <dd class="col-sm-9 fw-bold">{{ selectedInvoice.total | currency }}</dd>
                        @if (selectedInvoice.sourceEmailSubject) {
                            <dt class="col-sm-3">Email Subject</dt>
                            <dd class="col-sm-9">{{ selectedInvoice.sourceEmailSubject }}</dd>
                        }
                    </dl>

                    <div class="d-flex gap-3 small text-muted mb-3">
                        <span>{{ selectedInvoice.lineItems.length || 0 }} line(s)</span>
                        @if (selectedInvoice.receiptCount > 0) {
                            <span>
                                <a [routerLink]="['/purchasing/receiving']" [queryParams]="{ orderId: selectedInvoice.purchaseOrderId }">{{ selectedInvoice.receiptCount }} receipt(s)</a>
                            </span>
                        }
                    </div>

                    <h6>Line Items</h6>
                    <div class="table-responsive">
                        <table class="table table-sm mb-3">
                            <thead>
                                <tr>
                                    <th>Description</th>
                                    <th class="text-end">Ordered</th>
                                    <th class="text-end">Received</th>
                                    <th class="text-end">Invoiced (prior)</th>
                                    <th class="text-end">This Invoice</th>
                                    <th class="text-end">Unit Price</th>
                                    <th class="text-end">Line Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                @for (line of selectedInvoice.lineItems; track line.id) {
                                    <tr [ngClass]="lineInvoiceClass(line)">
                                        <td>{{ line.description }}</td>
                                        <td class="text-end">{{ line.quantityOrdered ?? '—' }}</td>
                                        <td class="text-end">{{ line.quantityReceivedToDate ?? '—' }}</td>
                                        <td class="text-end">{{ line.quantityInvoicedToDate ?? '—' }}</td>
                                        <td class="text-end">{{ line.quantity }}</td>
                                        <td class="text-end">{{ line.unitPrice | currency }}</td>
                                        <td class="text-end">{{ line.lineTotal | currency }}</td>
                                    </tr>
                                }
                            </tbody>
                        </table>
                    </div>

                    @if (selectedInvoice.documents && selectedInvoice.documents.length > 0) {
                        <h6>Documents</h6>
                        <ul class="list-unstyled small">
                            @for (doc of selectedInvoice.documents; track doc.id) {
                                <li>
                                    <a [href]="documentUrl(doc)" target="_blank">{{ doc.fileName }}</a>
                                    <span class="text-muted">({{ doc.documentType }})</span>
                                </li>
                            }
                        </ul>
                    }
                </div>
            </div>
        } @else {
            <div class="text-muted p-4 text-center border rounded">Select an invoice to view details, or create a new one manually or via AI scan.</div>
        }
    </div>
</div>
